<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { CircleStop } from 'lucide-vue-next'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDateTime, toApiDateTime } from '@yuksalish/ui'
import { useShopfloorApi } from '../composables/useShopfloorApi'
import type {
  Assignment,
  EmployeeOption,
  EquipmentItem,
  ShopfloorLot,
  ShopfloorOrder,
  ShopfloorStage,
} from '../types/shopfloor'

const { t } = useI18n()
const {
  listAssignments,
  createAssignment,
  endAssignment,
  listActiveOrders,
  listEmployees,
  listEquipment,
  listStages,
  listLots,
} = useShopfloorApi()

const activeTab = ref<'employee' | 'machine'>('employee')
const shiftFilter = ref('')
const showActiveOnly = ref(true)

const items = ref<Assignment[]>([])
const orders = ref<ShopfloorOrder[]>([])
const employees = ref<EmployeeOption[]>([])
const equipment = ref<EquipmentItem[]>([])
const orderStages = ref<ShopfloorStage[]>([])
const orderLots = ref<ShopfloorLot[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const submitting = ref(false)

const showCreate = ref(false)
const form = reactive({
  type: 'employee' as 'employee' | 'machine',
  shift: 'day',
  order_id: 0,
  stage_code: '',
  employee_id: null as number | null,
  machine_id: null as number | null,
  lot_number: '',
  assigned_at: new Date().toISOString().slice(0, 16),
  notes: '',
})

const tabItems = computed(() =>
  items.value.filter((a) => a.type === activeTab.value),
)
const visibleItems = computed(() =>
  tabItems.value.filter(
    (a) =>
      (!shiftFilter.value || a.shift === shiftFilter.value) &&
      (!showActiveOnly.value || a.status === 'active'),
  ),
)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listAssignments({ page: 1, per_page: 100 })
    items.value = result.items
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadOrders() {
  try {
    const page = await listActiveOrders({ status: 'in_progress', page: 1, per_page: 100 })
    orders.value = page.items
  } catch {
    orders.value = []
  }
}

async function loadEmployees() {
  try {
    employees.value = await listEmployees()
  } catch {
    employees.value = []
  }
}

async function loadEquipment() {
  try {
    equipment.value = await listEquipment()
  } catch {
    equipment.value = []
  }
}

async function onOrderChange() {
  orderStages.value = []
  orderLots.value = []
  form.stage_code = ''
  form.lot_number = ''
  if (!form.order_id) return
  try {
    const [stages, lots] = await Promise.all([
      listStages(form.order_id),
      listLots(form.order_id),
    ])
    orderStages.value = stages
    orderLots.value = lots
  } catch {
    orderStages.value = []
    orderLots.value = []
  }
}

watch(() => form.order_id, () => { void onOrderChange() })

onMounted(() => {
  void load()
  void loadOrders()
  void loadEmployees()
  void loadEquipment()
})

function openCreate() {
  form.type = activeTab.value
  form.shift = shiftFilter.value || 'day'
  form.order_id = orders.value[0]?.id ?? 0
  form.stage_code = ''
  form.employee_id = null
  form.machine_id = null
  form.lot_number = ''
  form.assigned_at = new Date().toISOString().slice(0, 16)
  form.notes = ''
  actionError.value = ''
  actionMessage.value = ''
  showCreate.value = true
  void onOrderChange()
}

async function submitCreate() {
  actionError.value = ''
  if (!form.order_id || !form.stage_code) {
    actionError.value = t('shopfloor.orderDetail.enterStageQuantity')
    return
  }
  submitting.value = true
  try {
    const created = await createAssignment({
      type: form.type,
      employee_id: form.type === 'employee' ? form.employee_id : null,
      machine_id: form.type === 'machine' ? form.machine_id : null,
      order_id: form.order_id,
      lot_number: form.lot_number.trim() || null,
      stage_code: form.stage_code,
      shift: form.shift,
      assigned_at: toApiDateTime(form.assigned_at),
      notes: form.notes.trim() || undefined,
    })
    items.value = [created, ...items.value]
    showCreate.value = false
    actionMessage.value = `${t('shopfloor.assignments.createAssignment')} #${created.id}`
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

async function end(assignment: Assignment) {
  actionError.value = ''
  if (!window.confirm(t('shopfloor.assignments.endConfirm'))) return
  submitting.value = true
  try {
    const ended = await endAssignment(assignment.id)
    items.value = items.value.map((a) => (a.id === ended.id ? ended : a))
    actionMessage.value = `${t('shopfloor.assignments.end')} #${assignment.id}`
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

function statusLabel(assignment: Assignment): string {
  return assignment.status === 'active'
    ? t('shopfloor.assignments.active')
    : t('shopfloor.assignments.ended')
}
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('shopfloor.assignments.eyebrow') }}</p>
      <h2>{{ t('shopfloor.assignments.title') }}</h2>
      <p>{{ t('shopfloor.assignments.description') }}</p>
    </div>
    <button class="btn-primary" @click="openCreate">{{ t('shopfloor.assignments.createAssignment') }}</button>
  </div>

  <!-- Tab bar -->
  <div class="tab-bar">
    <button :class="['tab', { active: activeTab === 'employee' }]" @click="activeTab = 'employee'">
      {{ t('shopfloor.assignments.employeeTab') }}
    </button>
    <button :class="['tab', { active: activeTab === 'machine' }]" @click="activeTab = 'machine'">
      {{ t('shopfloor.assignments.machineTab') }}
    </button>
  </div>

  <!-- Filters -->
  <div class="filter-bar">
    <select v-model="shiftFilter" class="filter-select">
      <option value="">{{ t('shopfloor.assignments.allShifts') }}</option>
      <option value="day">{{ t('shopfloor.assignments.day') }}</option>
      <option value="night">{{ t('shopfloor.assignments.night') }}</option>
    </select>
    <label class="check">
      <input v-model="showActiveOnly" type="checkbox" />
      {{ t('shopfloor.assignments.noEnded') }}
    </label>
  </div>

  <!-- Loading -->
  <div v-if="loading" class="state-message"><p>{{ t('shopfloor.assignments.loading') }}</p></div>

  <!-- Error -->
  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('shopfloor.state.retry') }}</button>
  </div>

  <!-- Empty -->
  <div v-else-if="visibleItems.length === 0" class="state-message">
    <p>{{ t('shopfloor.assignments.empty') }}</p>
  </div>

  <!-- Table -->
  <div v-else class="table-scroll">
  <table class="data-table">
    <thead>
      <tr>
        <th>{{ activeTab === 'employee' ? t('shopfloor.assignments.employee') : t('shopfloor.assignments.machine') }}</th>
        <th>{{ t('shopfloor.assignments.stageCode') }}</th>
        <th>{{ t('shopfloor.assignments.order') }}</th>
        <th>{{ t('shopfloor.assignments.lot') }}</th>
        <th>{{ t('shopfloor.assignments.shift') }}</th>
        <th>{{ t('shopfloor.assignments.assignedAt') }}</th>
        <th>{{ t('shopfloor.assignments.status') }}</th>
        <th>{{ t('shopfloor.assignments.operation') }}</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="a in visibleItems" :key="a.id">
        <td class="cell-name">{{ activeTab === 'employee' ? (a.employee_name ?? '—') : (a.machine_name ?? '—') }}</td>
        <td>{{ a.stage_name ?? a.stage_code }}</td>
        <td class="cell-code">{{ a.order_code ?? a.order_id }}</td>
        <td>{{ a.lot_number ?? '—' }}</td>
        <td>{{ t(`shopfloor.assignments.${a.shift === 'day' ? 'day' : 'night'}`) }}</td>
        <td>{{ formatDisplayDateTime(a.assigned_at) || '—' }}</td>
        <td>
          <span :class="['status-badge', a.status === 'active' ? 'status-active' : '']">
            {{ statusLabel(a) }}
          </span>
        </td>
        <td>
          <button v-if="a.status === 'active'" class="link-action icon-action danger" :title="t('shopfloor.assignments.end')" :aria-label="t('shopfloor.assignments.end')" @click="end(a)">
            <CircleStop :size="18" aria-hidden="true" />
          </button>
        </td>
      </tr>
    </tbody>
  </table>
  </div>

  <!-- Create modal -->
  <div v-if="showCreate" class="modal-overlay" @click.self="showCreate = false">
    <form class="modal" @submit.prevent="submitCreate">
      <h3>{{ t('shopfloor.assignments.createAssignment') }}</h3>
      <label>{{ t('shopfloor.assignments.type') }}
        <select v-model="form.type">
          <option value="employee">{{ t('shopfloor.assignments.employeeTab') }}</option>
          <option value="machine">{{ t('shopfloor.assignments.machineTab') }}</option>
        </select>
      </label>
      <label>{{ t('shopfloor.assignments.shift') }}
        <select v-model="form.shift">
          <option value="day">{{ t('shopfloor.assignments.day') }}</option>
          <option value="night">{{ t('shopfloor.assignments.night') }}</option>
        </select>
      </label>
      <label>{{ t('shopfloor.assignments.orderId') }}
        <select v-model.number="form.order_id" required>
          <option v-for="o in orders" :key="o.id" :value="o.id">{{ o.mo_code }}</option>
        </select>
      </label>
      <label>{{ t('shopfloor.assignments.stage') }}
        <select v-model="form.stage_code" required>
          <option value="" disabled>{{ t('shopfloor.assignments.selectStage') }}</option>
          <option v-for="s in orderStages" :key="s.stage_code" :value="s.stage_code">
            {{ s.stage_name }}
          </option>
        </select>
      </label>
      <label v-if="form.type === 'employee'">{{ t('shopfloor.assignments.employee') }}
        <select v-model.number="form.employee_id" required>
          <option :value="null" disabled>{{ t('shopfloor.assignments.selectEmployee') }}</option>
          <option v-for="e in employees" :key="e.id" :value="e.id">
            {{ e.first_name }} {{ e.last_name }}
          </option>
        </select>
      </label>
      <label v-else>{{ t('shopfloor.assignments.machine') }}
        <select v-model.number="form.machine_id" required>
          <option :value="null" disabled>{{ t('shopfloor.assignments.selectMachine') }}</option>
          <option v-for="e in equipment" :key="e.id" :value="e.id">
            {{ e.name }}
          </option>
        </select>
      </label>
      <label>{{ t('shopfloor.assignments.lot') }}
        <select v-model="form.lot_number">
          <option value="">{{ t('shopfloor.assignments.noLot') }}</option>
          <option v-for="l in orderLots" :key="l.lot_number" :value="l.lot_number">
            {{ l.lot_number }}
          </option>
        </select>
      </label>
      <label>{{ t('shopfloor.assignments.assignedAt') }}
        <input v-model="form.assigned_at" type="datetime-local" required />
      </label>
      <small v-if="actionError" class="error">{{ actionError }}</small>
      <div class="modal-actions">
        <button type="button" class="btn-secondary" @click="showCreate = false">{{ t('shopfloor.assignments.cancel') }}</button>
        <button type="submit" class="btn-primary" :disabled="submitting">
          {{ submitting ? t('shopfloor.assignments.creating') : t('shopfloor.assignments.create') }}
        </button>
      </div>
    </form>
  </div>

  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>
</template>

<style scoped>
.page-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }

.tab-bar {
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  border-bottom: 2px solid #e2e6e3;
}
.tab {
  padding: 10px 24px;
  font-size: 13px;
  font-weight: 600;
  color: #65736e;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}
.tab:hover { color: #1a2b26; }
.tab.active {
  color: #b3472d;
  border-bottom-color: #b3472d;
}

.filter-bar {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 20px;
}
.filter-select {
  padding: 8px 14px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
  min-width: 160px;
}
.check { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #1a2b26; }

.state-message {
  padding: 40px 0;
  text-align: center;
  color: #5f6b6a;
}
.state-message.error { color: #b33a3a; }

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  overflow: hidden;
}
.data-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #65736e;
  background: #f6f8f7;
  border-bottom: 1px solid #e2e6e3;
}
.data-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: #1a2b26;
  border-bottom: 1px solid #f0f2f1;
}
.data-table tr:last-child td { border-bottom: none; }
.cell-name { font-weight: 600; }
.cell-code { font-weight: 700; font-family: monospace; }

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: #eaf0ed;
  color: #3d5a4f;
}
.status-active { background: #dcefe7; color: #1f5c4b; }

.link-action {
  color: #27705d;
  font-weight: 600;
  font-size: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
.link-action:hover { text-decoration: underline; }
.link-action.danger { color: #b33a3a; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 20, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.modal {
  width: 380px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 90vh;
  overflow-y: auto;
}
.modal h3 { margin: 0; font-size: 16px; font-weight: 700; }
.modal label {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 600;
  color: #65736e;
  gap: 4px;
}
.modal input, .modal select {
  padding: 8px 12px;
  border: 1px solid #d4d8d5;
  border-radius: 6px;
  font-size: 13px;
  background: #fff;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.error { color: #b33a3a; font-size: 12px; }
.action-message {
  padding: 12px 16px;
  background: #dcefe7;
  color: #1f5c4b;
  border-radius: 8px;
  font-size: 13px;
  margin-top: 16px;
}

.btn-primary {
  display: inline-block;
  padding: 8px 20px;
  background: #27705d;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.btn-primary:disabled { opacity: 0.5; cursor: default; }
.btn-primary:hover:not(:disabled) { background: #1f5c4b; }
.btn-secondary {
  padding: 8px 16px;
  background: #fff;
  color: #27705d;
  border: 1px solid #27705d;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.btn-secondary:hover { background: #f0f5f3; }
</style>
